import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '@/components/AppHeader';
import { getAllMatches, deleteMatchHard, deleteMatchesByStatus, deleteAllMatchesHard, getOrphanMatches, assignMatchToTournament, discardMatch, getTournaments, lockMatch, unlockMatch, getTeams } from '@/lib/supabase';
import type { Team, Tournament } from '@/types';

interface MatchRow {
  id: string;
  matchNumber: number;
  broadcastId: string;
  status: string;
  date: string | null;
  matchType: string | null;
  teamAName: string;
  teamBName: string;
  tournamentId: string | null;
  tournamentName: string | null;
  isLocked: boolean;
  createdAt: string;
}

interface Stats {
  total: number;
  live: number;
  completed: number;
  discarded: number;
  pending: number;
  other: number;
}

type BulkAction = 'completed' | 'discarded' | 'all' | null;

interface OrphanMatch {
  id: string;
  match_number: number;
  date: string | null;
  match_type: string | null;
  team_a_id: string;
  team_b_id: string;
  status: string;
  created_at: string;
}

interface AdminMatchManagementProps {
  onSignOut: () => void;
}

export default function AdminMatchManagement({ onSignOut }: AdminMatchManagementProps) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, live: 0, completed: 0, discarded: 0, pending: 0, other: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkAction>(null);
  const [confirmText, setConfirmText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Orphan match repair state
  const [orphans, setOrphans] = useState<OrphanMatch[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showOrphanModal, setShowOrphanModal] = useState(false);
  const [selectedOrphan, setSelectedOrphan] = useState<OrphanMatch | null>(null);
  const [assignTournamentId, setAssignTournamentId] = useState<string>('');
  const [repairing, setRepairing] = useState(false);
  const [copiedBroadcastId, setCopiedBroadcastId] = useState<string | null>(null);

  const getPublicBaseUrl = () => {
    return import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin;
  };

  const getOverlayUrl = (broadcastId: string) => {
    return `${getPublicBaseUrl()}/overlay/${broadcastId}`;
  };

  const copyOverlayUrl = async (broadcastId: string) => {
    const url = getOverlayUrl(broadcastId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedBroadcastId(broadcastId);
      setTimeout(() => setCopiedBroadcastId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawMatches, rawTeams, rawTournaments] = await Promise.all([getAllMatches(), getTeams(), getTournaments()]);
      const teams: Map<string, Team> = new Map(
        rawTeams.map((t: any) => [t.id, { id: t.id, name: t.name, shortName: t.short_name, color: t.color, players: [] }])
      );
      const tournaments: Map<string, string> = new Map(
        rawTournaments.map((t: any) => [t.id, t.name])
      );

      const rows: MatchRow[] = rawMatches.map((m: any) => ({
        id: m.id,
        matchNumber: m.match_number,
        broadcastId: m.broadcast_id,
        status: m.status,
        date: m.date,
        matchType: m.match_type,
        teamAName: teams.get(m.team_a_id)?.shortName ?? '?',
        teamBName: teams.get(m.team_b_id)?.shortName ?? '?',
        tournamentId: m.tournament_id,
        tournamentName: m.tournament_id ? tournaments.get(m.tournament_id) ?? null : null,
        isLocked: m.is_locked ?? false,
        createdAt: m.created_at,
      }));

      setMatches(rows);
      setStats({
        total: rows.length,
        live: rows.filter(r => r.status === 'live' || r.status === 'set-won').length,
        completed: rows.filter(r => r.status === 'match-won').length,
        discarded: rows.filter(r => r.status === 'discarded').length,
        pending: rows.filter(r => r.status === 'setup' || r.status === 'first-server').length,
        other: rows.filter(r => !['live','set-won','match-won','discarded','setup','first-server'].includes(r.status)).length,
      });
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadOrphans = useCallback(async () => {
    try {
      const [orphanData, tournamentData, teamData] = await Promise.all([
        getOrphanMatches(),
        getTournaments(),
        getTeams(),
      ]);
      setOrphans(orphanData.map((m: any) => ({
        id: m.id,
        match_number: m.match_number,
        date: m.date,
        match_type: m.match_type,
        team_a_id: m.team_a_id,
        team_b_id: m.team_b_id,
        status: m.status,
        created_at: m.created_at,
      })));
      setTournaments(tournamentData);
      setTeams(teamData.map((t: any) => ({
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        players: [],
      })));
    } catch (err) {
      console.error('Failed to load orphans:', err);
    }
  }, []);

  useEffect(() => { loadOrphans(); }, [loadOrphans]);

  const handleAssignOrphan = async () => {
    if (!selectedOrphan || !assignTournamentId) return;
    setRepairing(true);
    try {
      await assignMatchToTournament(selectedOrphan.id, assignTournamentId);
      setShowOrphanModal(false);
      setSelectedOrphan(null);
      setAssignTournamentId('');
      await Promise.all([loadData(), loadOrphans()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign tournament');
    } finally {
      setRepairing(false);
    }
  };

  const handleDiscardOrphan = async () => {
    if (!selectedOrphan) return;
    setRepairing(true);
    try {
      await discardMatch(selectedOrphan.id);
      setShowOrphanModal(false);
      setSelectedOrphan(null);
      await Promise.all([loadData(), loadOrphans()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard match');
    } finally {
      setRepairing(false);
    }
  };

  const handleToggleLock = async (matchId: string, currentLockStatus: boolean) => {
    setDeleting(matchId);
    try {
      if (currentLockStatus) {
        await unlockMatch(matchId);
      } else {
        await lockMatch(matchId);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle lock');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteOne = async (id: string) => {
    setDeleting(id);
    try {
      await deleteMatchHard(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting('bulk');
    try {
      for (const id of selected) await deleteMatchHard(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkConfirm = async () => {
    if (bulkAction === 'all' && confirmText !== 'DELETE ALL') return;
    setDeleting('bulk-action');
    try {
      if (bulkAction === 'all') {
        await deleteAllMatchesHard();
      } else if (bulkAction === 'completed') {
        await deleteMatchesByStatus('match-won');
      } else if (bulkAction === 'discarded') {
        await deleteMatchesByStatus('discarded');
      }
      setBulkAction(null);
      setConfirmText('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setDeleting(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(m => m.id)));
    }
  };

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      live: 'Live', 'set-won': 'Set Won', 'match-won': 'Completed',
      discarded: 'Discarded', setup: 'Setup', 'first-server': 'Pending',
    };
    return map[s] ?? s;
  };

  const statusColors: Record<string, string> = {
    live: 'bg-warning/20 text-warning',
    'set-won': 'bg-warning/20 text-warning',
    'match-won': 'bg-success/20 text-success',
    discarded: 'bg-error/20 text-error',
    setup: 'bg-surface-raised text-text-muted',
    'first-server': 'bg-surface-raised text-text-muted',
  };

  const filtered = filterStatus === 'all' ? matches : matches.filter(m => {
    if (filterStatus === 'live') return m.status === 'live' || m.status === 'set-won';
    if (filterStatus === 'pending') return m.status === 'setup' || m.status === 'first-server';
    return m.status === filterStatus;
  });

  const statCards = [
    { label: 'Total', value: stats.total, color: 'text-text-primary' },
    { label: 'Live', value: stats.live, color: 'text-warning' },
    { label: 'Completed', value: stats.completed, color: 'text-success' },
    { label: 'Discarded', value: stats.discarded, color: 'text-error' },
    { label: 'Pending', value: stats.pending, color: 'text-text-muted' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="Match Management" navIcon="admin" onNavigate={() => navigate('/admin')} />

      <div className="flex-1 overflow-y-auto px-4 py-5 safe-area-bottom max-w-lg mx-auto w-full">
        {error && (
          <div className="bg-error/15 border border-error/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <p className="text-error text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-error text-lg font-bold ml-2">×</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-5 gap-2 mb-5">
          {statCards.map(s => (
            <div key={s.label} className="card text-center py-3">
              <p className={`text-lg font-bold ${s.color}`}>{loading ? '—' : s.value}</p>
              <p className="text-[10px] text-text-muted uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Orphan Matches Warning */}
        {orphans.length > 0 && (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full bg-warning animate-pulse" />
              <h3 className="text-sm font-bold text-warning uppercase tracking-wider">Orphan Matches Found</h3>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              {orphans.length} active match{orphans.length > 1 ? 'es' : ''} without a tournament. These are invisible in Tournament Dashboard.
            </p>
            <button
              onClick={() => setShowOrphanModal(true)}
              className="w-full py-2.5 px-4 rounded-lg text-sm font-bold bg-warning/20 text-warning border border-warning/40 hover:bg-warning/30 transition-colors active:scale-[0.99]"
            >
              Review & Repair ({orphans.length})
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="card mb-5">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Bulk Actions</p>
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={() => { setBulkAction('completed'); setConfirmText(''); }}
              disabled={stats.completed === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-40 hover:border-success/50 transition-colors active:scale-[0.99]"
            >
              Delete All Completed ({stats.completed})
            </button>
            <button
              onClick={() => { setBulkAction('discarded'); setConfirmText(''); }}
              disabled={stats.discarded === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-40 hover:border-warning/50 transition-colors active:scale-[0.99]"
            >
              Delete All Discarded ({stats.discarded})
            </button>
            <button
              onClick={() => { setBulkAction('all'); setConfirmText(''); }}
              disabled={stats.total === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-bold bg-error/10 border border-error/30 text-error disabled:opacity-40 hover:bg-error/20 transition-colors active:scale-[0.99]"
            >
              Delete ALL Matches ({stats.total})
            </button>
            {selected.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={!!deleting}
                className="py-2.5 px-4 rounded-lg text-sm font-bold bg-error/10 border border-error/30 text-error disabled:opacity-40 hover:bg-error/20 transition-colors active:scale-[0.99] flex items-center justify-center gap-2"
              >
                {deleting === 'bulk' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : `Delete Selected (${selected.size})`}
              </button>
            )}
          </div>
        </div>

        {/* Filter + List */}
        <div className="flex items-center gap-2 mb-3">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider flex-1">Matches</p>
          <select
            value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setSelected(new Set()); }}
            className="text-xs bg-surface border border-border rounded-lg px-2 py-1 text-text-primary"
          >
            <option value="all">All ({stats.total})</option>
            <option value="live">Live ({stats.live})</option>
            <option value="match-won">Completed ({stats.completed})</option>
            <option value="discarded">Discarded ({stats.discarded})</option>
            <option value="pending">Pending ({stats.pending})</option>
          </select>
          {filtered.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="text-xs text-text-muted underline"
            >
              {selected.size === filtered.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-10 border-dashed">
            <p className="text-text-muted text-sm">No matches in this category.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(m => (
              <div
                key={m.id}
                className={`card flex items-center gap-3 transition-colors ${selected.has(m.id) ? 'border-error/50 bg-error/5' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(m.id)}
                  onChange={() => toggleSelect(m.id)}
                  className="w-4 h-4 flex-shrink-0 accent-error"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-xs font-bold text-text-primary">#{m.matchNumber}</span>
                    <button
                      onClick={() => copyOverlayUrl(m.broadcastId)}
                      className="text-[10px] text-primary font-medium flex items-center gap-0.5 hover:text-primary/80"
                      title="Copy overlay URL"
                    >
                      <span>{m.broadcastId}</span>
                      {copiedBroadcastId === m.broadcastId ? (
                        <svg className="w-3 h-3 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColors[m.status] ?? 'bg-surface-raised text-text-muted'}`}>
                      {statusLabel(m.status)}
                    </span>
                    {m.status === 'match-won' && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.isLocked ? 'bg-surface-raised text-text-muted' : 'bg-warning/20 text-warning'}`}>
                        {m.isLocked ? '🔒 Locked' : '🔓 Unlocked'}
                      </span>
                    )}
                    {m.tournamentName && (
                      <span className="text-[10px] text-text-muted bg-surface-raised px-1.5 py-0.5 rounded truncate max-w-[150px]" title={m.tournamentName}>
                        {m.tournamentName}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-text-primary truncate">
                    {m.teamAName} <span className="text-text-muted font-normal text-xs">vs</span> {m.teamBName}
                  </p>
                  <p className="text-[10px] text-text-muted mt-0.5">
                    {m.date ?? new Date(m.createdAt).toLocaleDateString()}
                    {m.matchType ? ` · ${m.matchType}` : ''}
                  </p>
                </div>
                {/* Lock/Unlock button for completed matches */}
                {m.status === 'match-won' && (
                  <button
                    onClick={() => handleToggleLock(m.id, m.isLocked)}
                    disabled={!!deleting}
                    className={`flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-40 ${
                      m.isLocked
                        ? 'text-warning hover:bg-warning/10'
                        : 'text-text-muted hover:bg-surface-raised'
                    }`}
                    title={m.isLocked ? 'Unlock match' : 'Lock match'}
                  >
                    {deleting === m.id ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin block" />
                    ) : (
                      <span className="text-sm">{m.isLocked ? '🔓' : '🔒'}</span>
                    )}
                  </button>
                )}
                <button
                  onClick={() => handleDeleteOne(m.id)}
                  disabled={!!deleting}
                  className="flex-shrink-0 p-2 rounded-lg text-error hover:bg-error/10 transition-colors disabled:opacity-40"
                  title="Delete match"
                >
                  {deleting === m.id ? (
                    <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin block" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Action Confirmation Modal */}
      {bulkAction && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            {bulkAction === 'all' ? (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-1">Delete ALL Matches?</h3>
                <p className="text-sm text-text-secondary mb-4">
                  This will permanently delete all {stats.total} matches, including all sets and rally data. This cannot be undone.
                </p>
                <p className="text-sm font-semibold text-text-primary mb-2">
                  Type <span className="text-error font-bold">DELETE ALL</span> to confirm:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE ALL"
                  className="input-field mb-4 text-center font-bold tracking-widest"
                  autoFocus
                />
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-1">
                  Delete All {bulkAction === 'completed' ? 'Completed' : 'Discarded'} Matches?
                </h3>
                <p className="text-sm text-text-secondary mb-6">
                  This will permanently delete {bulkAction === 'completed' ? stats.completed : stats.discarded} matches and all their data. This cannot be undone.
                </p>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setBulkAction(null); setConfirmText(''); }}
                disabled={!!deleting}
                className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all active:scale-[0.98] disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleBulkConfirm}
                disabled={!!deleting || (bulkAction === 'all' && confirmText !== 'DELETE ALL')}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-error text-white transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {deleting === 'bulk-action' ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DELETING...
                  </span>
                ) : 'CONFIRM DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orphan Match Repair Modal */}
      {showOrphanModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-text-primary mb-1">Orphan Matches</h3>
            <p className="text-sm text-text-secondary mb-4">
              These matches have no tournament assigned. Assign them to a tournament or discard them.
            </p>

            {orphans.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-success font-semibold">No orphan matches found!</p>
                <button
                  onClick={() => setShowOrphanModal(false)}
                  className="mt-4 btn-secondary px-6 py-2"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {orphans.map(o => {
                  const teamA = teams.find(t => t.id === o.team_a_id);
                  const teamB = teams.find(t => t.id === o.team_b_id);
                  const isSelected = selectedOrphan?.id === o.id;

                  return (
                    <div
                      key={o.id}
                      className={`rounded-lg p-3 border ${
                        isSelected
                          ? 'bg-warning/10 border-warning/40'
                          : 'bg-surface-raised border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-text-muted">#{o.match_number}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${statusColors[o.status] ?? 'bg-surface-raised text-text-muted'}`}>
                          {statusLabel(o.status)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        {teamA && (
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamA.color }} />
                            <span className="text-sm font-semibold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                          </div>
                        )}
                        <span className="text-xs text-text-muted">vs</span>
                        {teamB && (
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamB.color }} />
                            <span className="text-sm font-semibold" style={{ color: teamB.color }}>{teamB.shortName}</span>
                          </div>
                        )}
                      </div>

                      {isSelected ? (
                        <div className="mt-3 pt-3 border-t border-border">
                          <label className="block text-xs text-text-muted mb-1">Assign to Tournament:</label>
                          <select
                            value={assignTournamentId}
                            onChange={e => setAssignTournamentId(e.target.value)}
                            className="input-field text-sm mb-3"
                          >
                            <option value="">Select Tournament...</option>
                            {tournaments.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() => { setSelectedOrphan(null); setAssignTournamentId(''); }}
                              disabled={repairing}
                              className="py-2 px-3 rounded-lg text-xs font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleDiscardOrphan}
                              disabled={repairing}
                              className="py-2 px-3 rounded-lg text-xs font-bold bg-error/10 text-error border border-error/30 disabled:opacity-50"
                            >
                              {repairing ? '...' : 'Discard'}
                            </button>
                            <button
                              onClick={handleAssignOrphan}
                              disabled={repairing || !assignTournamentId}
                              className="py-2 px-3 rounded-lg text-xs font-bold bg-success text-white disabled:opacity-50"
                            >
                              {repairing ? '...' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedOrphan(o)}
                          className="w-full py-2 text-xs font-semibold text-warning hover:bg-warning/10 rounded transition-colors"
                        >
                          Repair this match
                        </button>
                      )}
                    </div>
                  );
                })}

                <button
                  onClick={() => setShowOrphanModal(false)}
                  className="w-full mt-4 py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
